import { Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';

import { CalendarSubscribeOptions } from 'src/Components/Shared/CalendarSubscribeOptions';
import useFetchExportMetadata from 'src/Hooks/Queries/useFetchExportMetadata';
import { ExportWithEntitiesDtoEntityTypeEnum } from 'src/Services/API/generated';

import styles from './ExportSubscribePage.module.scss';

type ExportParams = {
  token: string;
};

const ExportSubscribePage = () => {
  const { t } = useTranslation();
  const { token } = useParams<ExportParams>();

  const { data, isLoading, isError } = useFetchExportMetadata(token);

  if (!token) return null;

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <Loader />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <img src="/icon-192.png" alt="Share Heritage" className={styles.logo} />
          <h1 className={styles.errorTitle}>
            {t('exportSubscribe.expiredTitle', 'Lien indisponible')}
          </h1>
          <p className={styles.errorBody}>
            {t(
              'exportSubscribe.expired',
              "Ce lien n'est plus valide. Demandez un nouveau lien à la personne qui vous l'a envoyé."
            )}
          </p>
        </div>
      </div>
    );
  }

  if (data.entityType !== ExportWithEntitiesDtoEntityTypeEnum.Event) {
    return <Navigate to={`/export/${token}`} replace />;
  }

  const title = data.assetName
    ? t('exportSubscribe.title', {
        defaultValue: 'Le calendrier de {{assetName}}',
        assetName: data.assetName,
      })
    : t('exportSubscribe.titleFallback', 'Le calendrier de la maison');

  const icalUrl = `${import.meta.env.VITE_API_URL}/public/exports/${token}/calendar.ics`;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <img src="/icon-192.png" alt="Share Heritage" className={styles.logo} />
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.intro}>
          {t(
            'exportSubscribe.intro',
            'Ajoutez-le à votre agenda : les séjours, travaux et jours importants apparaîtront chez vous et se mettront à jour automatiquement.'
          )}
        </p>

        <div className={styles.options}>
          <CalendarSubscribeOptions icalUrl={icalUrl} layout="page" context="public" />
        </div>

        <div className={styles.footer}>
          <Link to={`/export/${token}`} className={styles.viewOnly}>
            {t('exportSubscribe.viewOnly', "Voir le calendrier sans l'ajouter")}
          </Link>
          <p className={styles.readOnly}>
            {t(
              'exportSubscribe.readOnly',
              'Lecture seule. Vous ne pouvez rien modifier et personne ne voit vos données.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExportSubscribePage;
